import React, { useState } from 'react';
import { Button, SubmitButton } from '../../../../common/button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Edit } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import useNotification from '../../../../../core/hooks/use_notification';
import { Box, Divider, Grid, IconButton, LinearProgress, Tooltip } from '@mui/material';
import { getContactPersonAction, getContactPersonState } from '../../../../../features/food_partner/get_contact_person';
import { createContactPersonNotificationData, getContactPersonNotificationData, updateContactPersonNotificationData } from '../../../../../core/utils/notification_utils';
import { updateContactPersonAction, updateContactPersonState } from '../../../../../features/food_partner/update_contact_person';
import { createContactPersonAction, createContactPersonState } from '../../../../../features/food_partner/create_contact_person';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import TextFieldFormik from '../../../../common/text_field';
import RadioGroupFormik from '../../../../common/radio_group';
import { ApiRequestStatusEnum } from '../../../../../core/enums/common/api';

const contactPersonRoleOptions = [
    { value: 'COORDINATOR', label: 'Koordynator' },
    { value: 'MARKETING', label: 'Marketing' },
];

export default function ContactPersonDialog({ foodPartnerId, contactPersonId, refreshList, displaySmallIcon }) {
    const dispatch = useDispatch();
    const { showNotification, showErrorMessage } = useNotification();

    const [open, setOpen] = useState(false);
    const createForm = !contactPersonId;

    const getContactPerson = useSelector(getContactPersonState);
    const createContactPerson = useSelector(createContactPersonState);
    const updateContactPerson = useSelector(updateContactPersonState);

    const initialValues = {
        name: (!createForm && getContactPerson?.data?.name) || '',
        surname: (!createForm && getContactPerson?.data?.surname) || '',
        role: (!createForm && getContactPerson?.data?.role) || contactPersonRoleOptions[0].value,
        email: (!createForm && getContactPerson?.data?.email) || '',
        phoneNumber: (!createForm && getContactPerson?.data?.phoneNumber) || ''
    }

    const schema = Yup.object().shape({
        name: Yup.string().required(),
        surname: Yup.string().required(),
        role: Yup.string().required(),
        email: Yup.string().required(),
        phoneNumber: Yup.string()
    });

    const loadContactPerson = () => {
        if (!createForm) {
            dispatch(getContactPersonAction({ foodPartnerId, contactPersonId }))
                .then(response => showErrorMessage(getContactPersonNotificationData(response)));
        }
    };

    const onOpen = () => {
        loadContactPerson();
        setOpen(true);
    };

    const onSubmit = (values) => {
        const form = { ...values };

        if (createForm) {
            dispatch(createContactPersonAction({ form, foodPartnerId }))
                .then(response => {
                    showNotification(createContactPersonNotificationData(response));
                    if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                        onClose();
                        refreshList();
                    }
                });
        } else {
            dispatch(updateContactPersonAction({ form, foodPartnerId, contactPersonId }))
                .then(response => {
                    showNotification(updateContactPersonNotificationData(response));
                    if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                        onClose();
                        refreshList();
                    }
                });
        }
    };

    const onClose = () => {
        setOpen(false);
    };

    return (
        <>
            {
                displaySmallIcon ? (
                    <Tooltip title="Edytuj osobę kontaktową">
                        <IconButton size="small" color="primary" onClick={ onOpen }>
                            <Edit fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Button onClick={ onOpen }>
                        Dodaj osobę kontaktową
                    </Button>
                )
            }
            <Dialog open={ open } onClose={ onClose } fullWidth={ true } maxWidth="sm">
                <DialogTitle sx={ { fontWeight: 600, pb: 1 } }>Osoba kontaktowa</DialogTitle>
                <Divider/>

                <DialogContent sx={ { py: 3 } }>
                    { getContactPerson?.loading ? (
                        <Box mt={ 4 } mb={ 4 }>
                            <LinearProgress/>
                        </Box>
                    ) : (
                        <Formik
                            initialValues={ initialValues }
                            validationSchema={ schema }
                            validateOnChange={ false }
                            validateOnBlur={ false }
                            onSubmit={ onSubmit }>
                            { () => (
                                <Form>
                                    <Grid container spacing={ 2 }>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <TextFieldFormik name="name" label="Imię" required/>
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <TextFieldFormik name="surname" label="Nazwisko" required/>
                                        </Grid>
                                        <Grid item xs={ 12 }>
                                            <RadioGroupFormik
                                                name="role"
                                                label="Rola"
                                                options={ contactPersonRoleOptions }
                                                booleanOptions={ false }
                                            />
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <TextFieldFormik name="email" label="Email" required/>
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <TextFieldFormik name="phoneNumber" label="Numer telefonu"/>
                                        </Grid>
                                    </Grid>

                                    <Divider sx={ { mt: 3, mb: 2, mx: -3 } }/>

                                    <DialogActions sx={ { p: 0, gap: 1 } }>
                                        <Button variant="outlined" onClick={ onClose }>Zamknij</Button>
                                        <SubmitButton
                                            isLoading={ createForm ? createContactPerson?.loading : updateContactPerson?.loading }>Zapisz</SubmitButton>
                                    </DialogActions>
                                </Form>
                            ) }
                        </Formik>
                    ) }
                </DialogContent>
            </Dialog>
        </>
    );
}